// apps/accounts/src/pages/admin/StaffAttendanceAdmin.jsx
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Calendar, Filter, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

export default function StaffAttendanceAdmin() {
  const [attendance, setAttendance] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchData();
  }, [filterDate]);

  const fetchData = async () => {
    try {
      // Get all active staff (site_employees with isActive = true)
      const staff = await pb.collection('site_employees').getFullList({
        expand: 'user,site',
        filter: 'isActive = true',
      });
      setStaffList(staff);

      // Get attendance for the selected date
      const att = await pb.collection('staff_attendance').getFullList({
        filter: `date = "${filterDate}"`,
        expand: 'staff,leave_request',
      });
      setAttendance(att);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getStatus = (staffId) => {
    const record = attendance.find(a => a.staff === staffId);
    return record ? record.status : 'absent';
  };

  const getRecordId = (staffId) => {
    const record = attendance.find(a => a.staff === staffId);
    return record?.id || null;
  };

  const updateStatus = async (staffId, newStatus) => {
    try {
      const recordId = getRecordId(staffId);
      if (recordId) {
        await pb.collection('staff_attendance').update(recordId, { status: newStatus });
      } else {
        await pb.collection('staff_attendance').create({
          staff: staffId,
          date: filterDate,
          status: newStatus,
        });
      }
      toast.success(`Status set to ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error('Update failed: ' + err.message);
    }
  };

  if (loading) return <div className="p-6 text-center">Loading...</div>;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold">Staff Attendance (Admin)</h1>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="pl-9 w-48"
            />
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left">Staff</th>
                <th className="px-4 py-3 text-left">Site</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {staffList.map(s => {
                const status = getStatus(s.id);
                const isLeave = attendance.find(a => a.staff === s.id)?.leave_request;
                return (
                  <tr key={s.id} className="border-b hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-medium">{s.expand?.user?.name || s.id}</td>
                    <td className="px-4 py-3">{s.expand?.site?.name || '�'}</td>
                    <td className="px-4 py-3">
                      <Badge className={
                        status === 'present' ? 'bg-green-100 text-green-700' :
                        status === 'leave' ? 'bg-blue-100 text-blue-700' :
                        'bg-red-100 text-red-700'
                      }>
                        {status === 'present' && '? Present'}
                        {status === 'leave' && '?? Leave'}
                        {status === 'absent' && '? Absent'}
                      </Badge>
                      {isLeave && (
                        <span className="ml-2 text-xs text-blue-600">
                          (Leave approved)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-green-500 text-green-600 hover:bg-green-50"
                          onClick={() => updateStatus(s.id, 'present')}
                        >
                          Present
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-blue-500 text-blue-600 hover:bg-blue-50"
                          onClick={() => updateStatus(s.id, 'leave')}
                        >
                          Leave
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-500 text-red-600 hover:bg-red-50"
                          onClick={() => updateStatus(s.id, 'absent')}
                        >
                          Absent
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {staffList.length === 0 && (
            <div className="p-6 text-center text-slate-500">No active staff found</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}